"use client"

import { useState, useEffect } from "react"
import { useForm, SubmitHandler } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { PlusCircle, Edit, Trash2, Loader2 } from "lucide-react"
import { supabase } from "@/utils/supabase/client"
import { ExpenseCategory } from "@/types"
import { useAuth } from "@/contexts/auth-context"

const categorySchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters."),
  description: z.string().optional(),
  organization_id: z.string().uuid().optional(),
})

type CategoryFormData = z.infer<typeof categorySchema>

export function ExpenseCategories() {
  const { toast } = useToast()
  const { currentOrganization } = useAuth()
  const [categories, setCategories] = useState<ExpenseCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
  })

  useEffect(() => {
    if(currentOrganization) {
        fetchCategories()
    }
  }, [currentOrganization])
  
  useEffect(() => {
    if (selectedCategory) {
      setValue("name", selectedCategory.name)
      setValue("description", selectedCategory.description || "")
    } else {
      reset({ name: "", description: "" });
    }
  }, [selectedCategory, setValue, reset])

  const fetchCategories = async () => {
    if(!currentOrganization) return;
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*')
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      setCategories(data || [])
    } catch (error: any) {
      toast({ title: "Error", description: "Could not load categories. " + error.message, variant: "destructive" })
    } finally {
      setIsLoading(false)
    }
  }

  const handleFormSubmit: SubmitHandler<CategoryFormData> = async (data) => {
    if(!currentOrganization) return;
    setIsSubmitting(true);
    const payload = { ...data, organization_id: currentOrganization.id };

    try {
        let error;
        if (selectedCategory) {
            ({ error } = await supabase.from('expense_categories').update(payload).eq('id', selectedCategory.id));
        } else {
            ({ error } = await supabase.from('expense_categories').insert([payload]));
        }
        if(error) throw error;
      
      toast({ title: `Category ${selectedCategory ? 'updated' : 'created'}`, description: "Your category list is up to date." })
      fetchCategories()
      handleAddNew();
    } catch (error: any) {
      toast({ title: "Error", description: `Could not ${selectedCategory ? 'update' : 'create'} the category. ${error.message}`, variant: "destructive" })
    } finally {
        setIsSubmitting(false);
    }
  }

  const handleAddNew = () => {
    setSelectedCategory(null)
    reset({ name: "", description: ""});
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
        const { error } = await supabase.from('expense_categories').delete().eq('id', id);
        if(error) throw error;
        toast({ title: "Category Deleted", description: "The category has been removed." });
        fetchCategories();
    } catch(error: any) {
        toast({ title: "Error", description: `Could not delete category. ${error.message}`, variant: "destructive" });
    }
  }

  return (
    <div className="grid md:grid-cols-3 gap-6 mt-6">
      <div className="md:col-span-1">
        <Card>
          <CardHeader>
            <CardTitle>Expense Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={handleAddNew} className="w-full mb-4">
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Category
            </Button>
            {isLoading ? <Loader2 className="animate-spin mx-auto" /> : (
              <ul className="space-y-2">
                {categories.map(cat => (
                  <li key={cat.id} className="flex items-center justify-between p-2 rounded-md hover:bg-accent">
                    <span>{cat.name}</span>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => setSelectedCategory(cat)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="md:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>{selectedCategory ? "Edit Category" : "Add Category"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
              <div>
                <label htmlFor="name">Category Name</label>
                <Input id="name" {...register("name")} />
                {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
              </div>
              <div>
                <label htmlFor="description">Description</label>
                <Textarea id="description" {...register("description")} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleAddNew}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {selectedCategory ? "Save Changes" : "Save Category"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
} 